// First: drops an expired sign-in before anything creates the Supabase client.
import "./sessionBoot";
import React from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { hydrate as hydrateQueries, type DehydratedState } from "@tanstack/react-query";
import { initSentry, Sentry } from "./lib/sentry";
import { installDiagnostics } from "./lib/diagnostics";
import { captureAttribution } from "./lib/attribution";
import { queryClient } from "./lib/queryClient";
import { preloadLazy } from "./lib/lazyComponent";
import App from "./App.tsx";
import { isLinksHostMode } from "./seo/linksHost";
import "./index.css";

// Where this visitor came from, read before the router rewrites the URL.
captureAttribution();

// Initialize Sentry before rendering
initSentry();

// Before the first render, so the errors worth having — the ones thrown on
// the way up — are already in the buffer when someone opens a ticket.
installDiagnostics();

const rootEl = document.getElementById("root")!;
const tree = (
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>
);

// The links host renders another page than the HTML it may have been served
// (a preview with ?links_host=1 gets the prerendered home page): render, do not hydrate.
if (rootEl.hasAttribute("data-prerendered") && !isLinksHostMode(window.location.hostname, window.location.search)) {
  // Public page built as static HTML (scripts/prerender.mjs): take over the
  // markup already on screen instead of rendering it again. The page's public
  // data came along in the HTML, so the first render matches it.
  const page = window as unknown as { __RQ_STATE__?: DehydratedState; __PRERENDER_LAZY__?: string[] };
  if (page.__RQ_STATE__) hydrateQueries(queryClient, page.__RQ_STATE__);
  // The page's lazy chunks load first, so nothing suspends while hydrating
  // and nothing on screen is replaced by a loader. The page is already painted.
  void preloadLazy(page.__PRERENDER_LAZY__ ?? []).then(() => {
    hydrateRoot(rootEl, tree, {
      onRecoverableError(error) {
        // React re-rendered part of the page because it did not match the HTML.
        // The page still works; this is how a mismatch gets noticed and fixed.
        console.warn("[prerender] hydration mismatch", error);
        Sentry.captureException(error, { tags: { hydration: "mismatch" } });
      },
    });
  });
} else {
  createRoot(rootEl).render(tree);
}
